import React, { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import EditProduct from './pages/Products/productInfo/EditProduct';
import NotificationPage from './pages/Notification/Notification';
import Products from './pages/Products/productInfo/Products';
import ProductDetail from './pages/Products/productInfo/ProductDetail';
import UnProtectedRoute from './components/Layout/AuthGuard/UnProtectedRoute';
import ProtectedRoute from './components/Layout/AuthGuard/ProtectedRoute';
import { RoutesLink } from './utility/RouteLinks';
import AddCategory from './pages/Category/EditCategory';
import Categories from './pages/Category/Categories';
import AddBrands from './pages/Brands/EditBrand';
import Brands from './pages/Brands/Brands';
import RefundReturn from './pages/Orders/RefundReturn';
import Customers from './pages/UserManagement/Customer/Customer';
import AbandonedCheckouts from './pages/Orders/AbandonedCheckouts';
import CustomerDetails from './pages/UserManagement/Customer/CustomerDetails';
import Orders from './pages/Orders/Orders';
import AddProductCoupon from './pages/Products/discountAndCounpon/AddProductCoupon';
import CouponsList from './pages/Products/discountAndCounpon/CouponsList';
import DiscountProduct from './pages/Products/discountAndCounpon/DiscountProduct';
import AddProductDiscount from './pages/Products/discountAndCounpon/AddProductDiscount';
import OrderDetail from './pages/Orders/OrderDetail';
import AddSpinWheel from './pages/loyalaty/AddSpinWheel';
import SpinWheels from './pages/loyalaty/SpinWheels';
import CreateCollection from './pages/Products/Collection/CreateCollection';
import Collection from './pages/Products/Collection/Collection';
import CollectionDetails from './pages/Products/Collection/CollectionDetails';
import AddEmployeeDiscount from './pages/Settings/EmployeeDiscount/AddEmployeeDiscount';
import AddDriver from './pages/UserManagement/Customer/AddDriver';
import Staffs from './pages/UserManagement/staff/Staffs';
import Reports from './pages/reports/reports';
import WebstieContent from './pages/website/Content';
import AddRoles from './pages/UserManagement/roles/AddRoles';
import Roles from './pages/UserManagement/roles/Roles';
import ViewRole from './pages/UserManagement/roles/ViewRole';
import AddStaff from './pages/UserManagement/staff/AddStaff';
import Model from './components/common/models/Model';
import useNotificationHandler from './hooks/useNotificationHandler';
import { PERMISSIONS } from './context/permission';
import ReviewsDetails from './pages/ReviewRating/ReviewRatingDetails';
import { requestForToken } from './firebase-messaging';
import { getCookies, getToken, setCookies } from './utility/helper';
import { callApi } from './api/callApi';
import { API_ENDPOINT, API_URL } from './api/constant';
import RefundDetail from './pages/Orders/RefundDetail';
import AddContent from './pages/website/AddContent';
import Variants from './pages/Products/variants/Variants';
import ReviewRatingDetail from './pages/ReviewRating/ReviewRatingDetail';
import PrivacyPolicy from './pages/website/company/PrivacyPolicy';
import TermsAndCondation from './pages/website/company/TermsAndCondation';
import RefundReason from './pages/Settings/BasicInfo/RefundReason';
import SendNotification from './pages/Notification/SendNotification';
import ForgetPassword from './pages/Auth/ForegetPassword';
import AppLayout from './pages/Settings/BasicInfo/AppLayout';
import CategoriesManager from './pages/Category/CategoryManager';
import BrandManager from './pages/Brands/BrandManager';
import ProductManager from './pages/Products/productInfo/ProductManager';
import ViewCoupon from './pages/Products/discountAndCounpon/ViewCoupon';
const App: React.FC = () => {
  const location = useLocation();
  const { modal, orderNotification, setModal, dismissNotification, navigate } =
    useNotificationHandler();

  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, []);

  useEffect(() => {
    const initFCM = async () => {
      await updateFCMToken();
    };

    if ('serviceWorker' in navigator) {
      // console.log('serviceWorker' in navigator);
      initFCM();
    }
  }, [location.pathname]);

  const updateFCMToken = async () => {
    const token = await requestForToken();
    const existingToken = getCookies(
      import.meta.env.VITE_BUSINESS + 'deviceToken',
    );
    if (getToken() && token && existingToken !== token) {
      try {
        const PATH = `${API_URL}${API_ENDPOINT.session}/update/fcm`;
        await callApi(PATH, {
          method: 'PATCH',
          data: { token: token },
        });
        setCookies(import.meta.env.VITE_BUSINESS + 'deviceToken', token);
      } catch (error: any) {
        // console.log('Failed to update FCM token: ', error?.message);
      }
    }
  };

  useEffect(() => {
    const unlockAudio = () => {
      const audio = new Audio('/assets/audio/order-rec.wav');
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          // console.log('🔓 Audio autoplay unlocked');
        })
        .catch(console.warn);
      document.body.removeEventListener('click', unlockAudio);
    };
    document.body.addEventListener('click', unlockAudio);
    return () => document.body.removeEventListener('click', unlockAudio);
  }, []);

  return (
    <>
      <Routes>
        <Route element={<UnProtectedRoute />}>
          <Route path="/" element={<Login />} />
          <Route
            path={RoutesLink.forgetPassword}
            element={<ForgetPassword />}
          />
        </Route>
        <Route element={<ProtectedRoute />}>
          <Route path={RoutesLink.dashboard} element={<Dashboard />} />
          <Route
            path={RoutesLink.notifications}
            element={<NotificationPage />}
          />

          {/* PRODUCT LISTING  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_PRODUCTS} />}
          >
            <Route path={RoutesLink.products} element={<Products />} />
          </Route>

          {/* EDIT PRODUCT  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.EDIT_PRODUCTS} />}
          >
            <Route
              path={`${RoutesLink.addProducts}/:id`}
              element={<EditProduct />}
            />
          </Route>

          {/* ADD CATEGORY  */}
          <Route
            element={
              <ProtectedRoute permissions={PERMISSIONS.EDIT_CATEGORIES} />
            }
          >
            <Route
              path={`${RoutesLink.addCategory}/:id`}
              element={<AddCategory />}
            />
          </Route>

          {/* VIEW CATEGORY  */}
          <Route
            element={
              <ProtectedRoute permissions={PERMISSIONS.VIEW_CATEGORIES} />
            }
          >
            <Route path={RoutesLink.categoryList} element={<Categories />} />
            <Route
              path={RoutesLink.categoryManager}
              element={<CategoriesManager />}
            />
          </Route>

          {/* EDIT BRANDS  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.EDIT_BRANDS} />}
          >
            <Route
              path={`${RoutesLink.addBrands}/:id`}
              element={<AddBrands />}
            />
          </Route>

          {/* VIEW BRANDS  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_BRANDS} />}
          >
            <Route path={RoutesLink.brandsList} element={<Brands />} />
            <Route path={RoutesLink.brandManager} element={<BrandManager />} />
          </Route>

          {/* VIEW PRODUCTS  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_PRODUCTS} />}
          >
            <Route
              path={`${RoutesLink.productDetail}/:id`}
              element={<ProductDetail />}
            />
            <Route
              path={`${RoutesLink.productManager}`}
              element={<ProductManager />}
            />
          </Route>

          {/* VARIANTS LIST */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_PRODUCTS} />}
          >
            <Route path={`${RoutesLink.variants}`} element={<Variants />} />
          </Route>

          {/* REFUNT AND RETURN  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_ORDERS} />}
          >
            <Route path={RoutesLink.refundReturn} element={<RefundReturn />} />
          </Route>

          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_ORDERS} />}
          >
            <Route
              path={`${RoutesLink.refundDetail}/:refundId`}
              element={<RefundDetail />}
            />
          </Route>

          {/* VIEW ORDERS  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_ORDERS} />}
          >
            <Route path={RoutesLink.order} element={<Orders />} />
          </Route>

          {/* VIEW ORDERS  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_ORDERS} />}
          >
            <Route
              path={`${RoutesLink.orderDetail}/:id`}
              element={<OrderDetail />}
            />
          </Route>

          <Route path={RoutesLink.customer} element={<Customers />} />
          <Route path={RoutesLink.addDriver} element={<AddDriver />} />
          <Route path={RoutesLink.discounts} element={<DiscountProduct />} />
          <Route
            path={RoutesLink.addDiscount}
            element={<AddProductDiscount />}
          />
          <Route
            element={
              <ProtectedRoute permissions={PERMISSIONS.CREATE_DISCOUNT} />
            }
          >
            <Route
              path={`${RoutesLink.addCoupon}`}
              element={<AddProductCoupon />}
            />
          </Route>
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.EDIT_DISCOUNT} />}
          >
            <Route
              path={`${RoutesLink.addCoupon}/:code`}
              element={<AddProductCoupon />}
            />
          </Route>
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_DISCOUNT} />}
          >
            <Route path={RoutesLink.coupons} element={<CouponsList />} />
            <Route
              path={`${RoutesLink.coupons}/:code`}
              element={<ViewCoupon />}
            />
          </Route>

          {/* ADD DAILY SPIN  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.CREATE_SPIN} />}
          >
            <Route
              path={`${RoutesLink.addSpinWheel}`}
              element={<AddSpinWheel />}
            />
          </Route>

          {/* EDIT DAILY SPIN  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.EDIT_SPIN} />}
          >
            <Route
              path={`${RoutesLink.addSpinWheel}/:id`}
              element={<AddSpinWheel />}
            />
          </Route>

          {/* VIEW DAILY SPIN  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_SPIN} />}
          >
            <Route path={RoutesLink.spinWheels} element={<SpinWheels />} />4
          </Route>

          {/* VIEW CUSTOMER DETAIL  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_USERS} />}
          >
            <Route
              path={`${RoutesLink.customerDetails}/:id`}
              element={<CustomerDetails />}
            />
          </Route>

          {/* ABANDONED CHECKOUTS */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_ORDERS} />}
          >
            <Route
              path={RoutesLink.abandonedCheckouts}
              element={<AbandonedCheckouts />}
            />
          </Route>

          {/* REVIEW  AND RATTING */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_REVIEW} />}
          >
            <Route
              path={RoutesLink.reviewRatingDetails}
              element={<ReviewsDetails />}
            />
          </Route>

          {/*VIEW REVIEW  AND RATTING DETAIL */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_REVIEW} />}
          >
            <Route
              path={`${RoutesLink.reviewRatingDetails}/:id`}
              element={<ReviewRatingDetail />}
            />
          </Route>

          {/* CREATE COLLECTION  */}
          <Route
            element={
              <ProtectedRoute permissions={PERMISSIONS.CREATE_COLLECTIONS} />
            }
          >
            <Route
              path={`${RoutesLink.createCollection}`}
              element={<CreateCollection />}
            />
          </Route>

          {/* EDIT COLLECTION  */}
          <Route
            element={
              <ProtectedRoute permissions={PERMISSIONS.CREATE_COLLECTIONS} />
            }
          >
            <Route
              path={`${RoutesLink.createCollection}/:id`}
              element={<CreateCollection />}
            />
          </Route>

          {/* VIEW COLLECTION  */}
          <Route
            element={
              <ProtectedRoute permissions={PERMISSIONS.VIEW_COLLECTIONS} />
            }
          >
            <Route path={`${RoutesLink.collection}`} element={<Collection />} />
          </Route>

          <Route
            path={`${RoutesLink.createCollection}`}
            element={<CreateCollection />}
          />
          <Route path={`${RoutesLink.collection}`} element={<Collection />} />
          <Route
            path={`${RoutesLink.collectionDetails}/:id`}
            element={<CollectionDetails />}
          />
          <Route
            path={`${RoutesLink.createEmpDiscount}`}
            element={<AddEmployeeDiscount />}
          />
          <Route path={`${RoutesLink.staffs}`} element={<Staffs />} />
          <Route path={`${RoutesLink.reports}`} element={<Reports />} />

          {/* VIEW WEBSITE CONTENT LIST  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_CONTENT} />}
          >
            <Route
              path={`${RoutesLink.websiteContent}`}
              element={<WebstieContent />}
            />
          </Route>

          {/* ADD WEBSITE CONTENT  */}
          <Route
            element={
              <ProtectedRoute permissions={PERMISSIONS.CREATE_CONTENT} />
            }
          >
            <Route path={RoutesLink.addContent} element={<AddContent />} />
          </Route>

          {/* EDIT WEBSITE CONTENT  */}
          <Route
            element={
              <ProtectedRoute permissions={PERMISSIONS.CREATE_CONTENT} />
            }
          >
            <Route
              path={`${RoutesLink.addContent}/:id`}
              element={<AddContent />}
            />
          </Route>

          {/* GET LAYOUT FOR APP HOME  */}
          <Route
            element={
              <ProtectedRoute permissions={PERMISSIONS.CREATE_CONTENT} />
            }
          >
            <Route path={`${RoutesLink.appLayout}`} element={<AppLayout />} />
          </Route>

          {/* REFUND REASON  */}
          <Route
            element={<ProtectedRoute permissions={PERMISSIONS.VIEW_ORDERS} />}
          >
            <Route
              path={`${RoutesLink.refundReason}`}
              element={<RefundReason />}
            />
          </Route>

          <Route
            path={RoutesLink.sendNotification}
            element={<SendNotification />}
          />
          <Route path={RoutesLink.roles} element={<Roles />} />
          <Route path={`${RoutesLink.viewRole}/:id`} element={<ViewRole />} />
          <Route path={`${RoutesLink.addRoles}/:id`} element={<AddRoles />} />
          <Route path={RoutesLink.addRoles} element={<AddRoles />} />
          <Route path={`${RoutesLink.addStaff}`} element={<AddStaff />} />
          <Route path={`${RoutesLink.addStaff}/:id`} element={<AddStaff />} />
          <Route
            path={`${RoutesLink.privacyPolicy}`}
            element={<PrivacyPolicy />}
          />
          <Route
            path={`${RoutesLink.termsCondation}`}
            element={<TermsAndCondation />}
          />
        </Route>
      </Routes>
      <Model
        show={modal}
        onClose={() => {
          setModal(false);
          dismissNotification();
        }}
        title={'Nass Foods'}
        size="max-w-[500px] w-full"
      >
        <div className="text-center px-6 py-4">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            🛒 New Order Received
          </h2>
          <p className="text-gray-600 text-sm mb-6">
            You have a new incoming order. Would you like to view the order
            details now?
          </p>

          <div className="grid grid-cols-2 gap-4">
            <button
              className="w-full btn btn-primary px-6 py-2"
              onClick={() => {
                dismissNotification();
                setModal(false);
                navigate(`${RoutesLink.orderDetail}/${orderNotification?.id}`);
              }}
            >
              View Order
            </button>
            <button
              className="btn btn-secondary w-full"
              onClick={() => {
                setModal(false);
                dismissNotification();
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default App;
